import axiosInstance from "../../utils/axiosConfig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../components/data-table";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import {
  buildClientRows,
  CLIENTS_TABLE_LIMIT,
  createClientColumns,
  type ClientRecord,
  type ClientRow,
} from "./clientsTable";

const Clients = ({ refetchCount, setClientId, setSuccessClientModal }: any) => {
  const { isAuthenticated, query } = useAuth();
  const [page, setPage] = useState<number>(1);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCustomers, setTotalCustomers] = useState<number>(0);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);

    const fetchClients = () => {
      const searchParam = query ? `&search=${query}` : "";
      axiosInstance
        .get(
          `${API_URL}api/users/clients/?limit=${CLIENTS_TABLE_LIMIT}&page=${page}${searchParam}`
        )
        .then((r: any) => {
          setTotalPages(Math.max(1, r.data.data.meta.totalPages ?? 1));
          setTotalCustomers(r.data.data.meta.totalUsers ?? 0);
          setClients(
            Array.isArray(r.data.data.clients) ? r.data.data.clients : []
          );
        })
        .catch((e: any) => {
          console.log(e?.response?.data?.message || "An error occurred");
        })
        .finally(() => setLoading(false));
    };

    const debounceTimer = setTimeout(fetchClients, 600);
    return () => clearTimeout(debounceTimer);
  }, [page, isAuthenticated, query, refetchCount]);

  const openClient = useCallback(
    (row: ClientRow) => {
      setClientId(row.raw._id);
      setSuccessClientModal(true);
    },
    [setClientId, setSuccessClientModal]
  );

  const columns = useMemo(() => createClientColumns(openClient), [openClient]);
  const rows = useMemo<ClientRow[]>(() => buildClientRows(clients), [clients]);

  if (loading && clients.length === 0) {
    return (
      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  return (
    <div className="w-full pb-10">
      {loading && (
        <p className="mb-2 text-[12px] text-[#869AA9]">Updating list…</p>
      )}
      <DataTable<ClientRow>
        columns={columns}
        data={rows}
        currentPage={page}
        setCurrentPage={setPage}
        totalPage={totalPages}
        count={totalCustomers}
        limit={CLIENTS_TABLE_LIMIT}
        onRowClick={openClient}
        emptyContent="No clients to display."
        className="overflow-hidden rounded-xl border border-[#2C363E]"
        fixedBodyHeight="min(560px, 55vh)"
      />
    </div>
  );
};

export default Clients;
